import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import isLogged from 'utils/isLogged';
import useError from 'hooks/useError';

type Props = {
  children: ReactNode;
};

type ClientBlockedStatus = {
  blocked: boolean;
  blockedUntil: string | null;
};

export type ClientBlockedStatusState = ClientBlockedStatus | undefined | null;

type ClientBlockedStatusContextType = {
  clientBlockedStatus: ClientBlockedStatusState;
  fetchClientBlockedStatus: () => Promise<ClientBlockedStatusState>;
};

const ClientBlockedStatusContext =
  createContext<ClientBlockedStatusContextType>(
    {} as ClientBlockedStatusContextType,
  );

const useClientBlockedStatus = (): ClientBlockedStatusContextType =>
  useContext(ClientBlockedStatusContext);

const ClientBlockedStatusProvider = ({ children }: Props): JSX.Element => {
  const [clientBlockedStatus, setClientBlockedStatus] =
    useState<ClientBlockedStatusState>();
  const { showError } = useError();

  const fetchClientBlockedStatus =
    useCallback(async (): Promise<ClientBlockedStatus> => {
      try {
        const { data: fetchedClientBlockedStatus } = await axiosInstance.get(
          '/client/blockedStatus',
        );
        setClientBlockedStatus(fetchedClientBlockedStatus);
        return fetchedClientBlockedStatus;
      } catch (e) {
        setClientBlockedStatus(null);
        showError();
        throw e;
      }
    }, [showError]);

  useEffect(() => {
    if (clientBlockedStatus === undefined && isLogged())
      fetchClientBlockedStatus();
  }, []);

  const clientBlockedStatusContextValue = useMemo(
    () => ({
      clientBlockedStatus,
      fetchClientBlockedStatus,
    }),
    [clientBlockedStatus, fetchClientBlockedStatus],
  );

  return (
    <ClientBlockedStatusContext.Provider
      value={clientBlockedStatusContextValue}
    >
      {children}
    </ClientBlockedStatusContext.Provider>
  );
};

export { useClientBlockedStatus as default, ClientBlockedStatusProvider };
